/**
 * quizProgressStorage.ts — WEB-2177: покупательский прогресс квиза переживает
 * перезагрузку страницы, случайное закрытие вкладки и выгрузку вкладки
 * телефоном.
 *
 * Решение владельца (16.09, зафиксировано в задаче): прогресс хранится ТОЛЬКО
 * в браузере покупателя, на сервер ничего не уходит. Ответы квиза бывают про
 * здоровье, вес и привычки — отправка на сервер завела бы эту работу в линию
 * про чувствительные данные (WEB-2158..WEB-2163) без выигрыша для покупателя,
 * который тот же самый получаем локально.
 *
 * Ключ ОБЯЗАН включать версию опубликованного содержимого (`versionId` —
 * id последнего снимка публикации квиза, WEB-848 D3, свежий на каждой
 * республикации). Без версии в ключе республикация со сменой экранов увела
 * бы человека на несуществующий или неподходящий экран — это главный риск
 * задачи. Версия не совпала — тихо начинаем заново, без пугающих сообщений
 * (`loadQuizProgress` в этом случае просто возвращает undefined).
 *
 * Что здесь НЕ хранится:
 *  - ничего денежного — цену, доступность тарифа и состояние оплаты всегда
 *    считает заново сервер; восстановить можно только выбранный тариф, а он
 *    приходит через `variables` (ответы квиза), а не отдельным полем here;
 *  - ничего личного из истории переходов: с 18.09 здесь лежат номера экранов
 *    (`history`) — и только они, обычные числа.
 *
 * ЖАЛОБА ПОКУПАТЕЛЯ 18.09.2026 (воронка funnel.quizsomaapp.info) поправила два
 * решения этого файла:
 *  1) завершённый проход (`isCompleted`) больше не восстанавливается сам по
 *     себе — только когда страница открыта штатным шагом оплаты (см.
 *     `loadQuizProgress`); иначе механизм обходил защиту WEB-1740 и запирал
 *     квиз на сутки для всех, кто однажды дошёл до пейволла;
 *  2) история переходов теперь хранится: без неё после перезагрузки пропадала
 *     кнопка «Назад». Раньше это было записано здесь как сознательно принятая
 *     цена; владелец 18.09 назвал это дефектом.
 *
 * Согласие (линия WEB-2145/2146, `euConsent.ts`): явный отказ от хранения
 * (`w2a_consent=denied`) выключает и запись, и чтение. Посетитель вне списка
 * стран (баннер не показывался, решения нет) или явное согласие — хранение
 * разрешено.
 *
 * Хранилище трогаем только через try/catch — тот же приём, что в
 * `paywallStepProof.ts` (WEB-1740): в приватном режиме Safari и при
 * заблокированных данных сайта само обращение к `window.localStorage`
 * бросает исключение.
 */

import { readConsentDecision } from '@/utils/analytics/euConsent';
import { FUNNEL_BROWSER_STATE_TTL_MS } from './funnelBrowserStateTtl';
import { SENSITIVE_CONSENT_BLOCK_TYPE } from './sensitiveConsent';

const KEY_PREFIX = 'w2a_quiz_progress:';

/**
 * Сутки — срок жизни сохранённого прогресса (карточка WEB-2177, п.3).
 *
 * Число берётся из общего места (`funnelBrowserStateTtl.ts`) и НЕ повторяется
 * здесь: тем же сроком живёт след шага оплаты (`paywallStepProof.ts`), а две
 * половины одного факта «где человек сейчас в этой воронке» с разными сроками
 * разъезжаются молча — ровно это и вскрыла жалоба покупателя 18.09.2026. Имя
 * `QUIZ_PROGRESS_TTL_MS` оставлено как есть: им пользуется соседний код.
 */
export const QUIZ_PROGRESS_TTL_MS = FUNNEL_BROWSER_STATE_TTL_MS;

/**
 * WEB-2177 / WEB-1716 (находка 4 свежего взгляда, проход 5) — НЕ ДОЕХАВШИЙ
 * ЛИД. Второй рубеж отправки лида (`pendingLeadRef` в QuizScreenWizard) жил
 * только в памяти вкладки, а признак «почту забрали» уезжал сюда СРАЗУ, не
 * дожидаясь ответа сервера. Перезагрузка страницы между отказом сервера и
 * повторной попыткой давала худший исход: признак восстанавливался как
 * «забрали», повторов не было, контакт терялся навсегда.
 *
 * Поэтому незавершённая попытка едет в том же снимке прогресса: `payload` —
 * ровно то тело, которое уходило в `captureLeadPublic` (плоский объект строк,
 * см. `CaptureLeadPayload`; типизирован здесь структурно, чтобы модуль
 * хранилища не зависел от слоя api), `retries` — число уже сделанных повторов,
 * чтобы потолок `MAX_PENDING_LEAD_RETRIES` не обнулялся перезагрузкой.
 *
 * Денежного здесь по-прежнему нет (докблок файла): адрес почты покупатель сам
 * только что ввёл в этой вкладке, а цену/оплату всегда считает сервер.
 */
export interface PendingLeadSnapshot {
  /**
   * Тело отправки. Типом здесь зафиксирован только `email` — он единственный
   * обязательный и единственный, который читает/проверяет этот модуль. В
   * РАНТАЙМЕ здесь лежит ВЕСЬ `CaptureLeadPayload` целиком (utm, sessionId,
   * consent): объект кладётся и достаётся как есть, и таким же уходит в
   * повторную отправку. Сильнее типизировать нельзя без зависимости хранилища
   * от слоя api — `CaptureLeadPayload` интерфейс без индексной подписи, и
   * `Record<string, unknown>` его не принимает.
   */
  payload: { email: string };
  retries: number;
}

/**
 * Ровно то, что карточка разрешает хранить — ни цены, ни статуса оплаты.
 *
 * WEB-2177 (находка 1 свежего взгляда, проход 3): `values` — НАСТОЯЩИЕ ответы
 * покупателя (FormValuesContext.values, ключ = id блока). `variables` — это
 * отдельный F1b VARIABLE-стейт визарда (сейчас всегда пуст, см. докблок
 * QuizScreenWizard.tsx рядом с сейв-эффектом) — сохраняется как было, ответы
 * в нём не жили никогда.
 */
export interface QuizProgressSnapshot {
  screenIndex: number;
  /**
   * ЖАЛОБА ПОКУПАТЕЛЯ 18.09.2026 (funnel.quizsomaapp.info), вторая половина:
   * номера экранов, пройденных до текущего. Раньше история переходов сюда не
   * попадала сознательно (см. докблок файла выше), и после перезагрузки
   * кнопка «Назад» пропадала — визард считал восстановленный экран первым.
   * Владелец 18.09 назвал это дефектом, а не принятой ценой.
   *
   * Личных данных здесь нет по построению: только номера экранов.
   */
  history: number[];
  /**
   * ЖАЛОБА ПОКУПАТЕЛЯ 18.09.2026, третий член того же класса: отметки
   * «на этом экране авто-переход уже сработал» (useAutoAdvance.ts). Они жили
   * только в памяти вкладки, а ответы покупателя перезагрузку переживали — и
   * «Назад» на экран с выбором варианта немедленно уносило вперёд.
   *
   * Формат отметки — `экран:блок`, БЕЗ ответа покупателя. В памяти вкладки
   * ключ включает и значение ответа (`экран:блок:значение`) — именно оно
   * отличает «тот же ответ» от «человек сменил выбор». Здесь значение не
   * хранится: при восстановлении оно берётся из уже восстановленных ответов
   * (`values`) того же снимка. Поэтому в браузере не появляется ни одного
   * нового байта покупательского ввода — в том числе в воронке, где ответы
   * исключены решением владельца 17.09 (там отметка просто не сработает, и
   * поведение остаётся прежним).
   */
  autoAdvanced: string[];
  variables: Record<string, string>;
  values: Record<string, string>;
  isCompleted: boolean;
  /** ПОДТВЕРЖДЁННЫЙ сервером захват почты — не «мы отправили», а «приняли». */
  emailCaptured: boolean;
  /** Отправка, которую сервер не принял; `undefined` — нечего добивать. */
  pendingLead?: PendingLeadSnapshot;
}

/**
 * История переходов из записи в браузере покупателя. Поле опционально НА
 * ЧТЕНИИ (записи, сделанные до жалобы 18.09, его не несут — это не повод
 * считать запись битой, тот же приём, что уже сделан для `values`), а негодная
 * форма трактуется как «истории нет»: терять из-за неё номер экрана и ответы
 * покупателя было бы дороже, чем потерять кнопку «Назад».
 */
function readHistory(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return value.every((item) => typeof item === 'number') ? (value as number[]) : [];
}

/**
 * Отметки сработавшего авто-перехода из записи в браузере. Те же правила, что
 * у `history`: поле необязательное на чтении (старые записи его не несут),
 * негодная форма читается как «отметок нет» — цена ошибки здесь мала
 * (максимум один лишний прыжок вперёд), а потеря всей записи дорога.
 */
function readAutoAdvanced(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.every((item) => typeof item === 'string')
    ? (value as string[])
    : [];
}

/**
 * То, что чтение отдаёт наружу: снимок плюс ответ на вопрос «это продолжение
 * прохода или мы начали заново».
 *
 * НАХОДКА 2 СВЕЖЕГО ВЗГЛЯДА. Сброшенный завершённый проход — это НОВЫЙ проход,
 * и читающему нужно уметь его отличить. Без этого признака визард поднимал
 * «почту уже отправляли» от восстановленной недоотправки, покупатель проходил
 * квиз заново, вводил почту — и отправка молча выходила: ни лида, ни события,
 * ни письма. А если у старой недоотправки исчерпаны повторы, терялись ОБА
 * контакта.
 *
 * Поле НЕ хранимое: оно про обстоятельства конкретного чтения, а не про
 * состояние покупателя. Поэтому его нет в `QuizProgressSnapshot` (тип записи)
 * и оно не может случайно уехать в localStorage вместе со снимком.
 */
export interface RestoredQuizProgress extends QuizProgressSnapshot {
  restartedAfterCompletion?: true;
}

/** Разбор записи из хранилища: доверять форме чужой записи нельзя. */
function isValidPendingLead(value: unknown): value is PendingLeadSnapshot {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (typeof v.retries !== 'number') return false;
  if (!v.payload || typeof v.payload !== 'object') return false;
  return typeof (v.payload as Record<string, unknown>).email === 'string';
}

interface StoredQuizProgress extends QuizProgressSnapshot {
  savedAt: number;
  /**
   * НАХОДКА 1 ТРЕТЬЕГО ПРОХОДА СВЕЖЕГО ВЗГЛЯДА: в записи не осталось
   * прохождения — только недоотправленный лид, который некому больше
   * сохранить (см. ветку сброса в `loadQuizProgress`).
   *
   * ПОЧЕМУ ЭТО ПОЛЕ ХРАНИМОЕ, в отличие от `restartedAfterCompletion`. Оно
   * описывает САМУ ЗАПИСЬ («за мной прохождения нет»), а не обстоятельства
   * конкретного чтения. Без него ВТОРОЙ читающий той же загрузки (визард
   * читает после страницы) видел бы обычную запись с недоотправкой, поднимал
   * бы признак «почту уже отправляли» — и почта, введённая на новом проходе,
   * не ушла бы вовсе. Запись, сделанная `saveQuizProgress`, это поле не несёт
   * никогда: список полей там поимённый.
   */
  leadOnly?: true;
}

function storageKey(
  projectId: string,
  quizId: string,
  versionId: string,
): string {
  return `${KEY_PREFIX}${projectId}:${quizId}:${versionId}`;
}

/**
 * WEB-2177 — РЕШЕНИЕ ВЛАДЕЛЬЦА 17.09.2026, дословно: «не держать в браузере
 * покупателя почту, телефон и поля веса/роста; ответы с вариантами оставить».
 *
 * ПОЧЕМУ ТИПЫ, А НЕ СПИСОК КЛЮЧЕЙ. Ключ в `values` — это id блока, у каждой
 * воронки свой (uuid, новый на каждый созданный блок). Списком ключей
 * «почта/телефон/вес/рост» решение не выразить в принципе: он был бы верен
 * ровно для одной воронки и молчал бы про все остальные. Поэтому решение
 * записано ТИПАМИ полей ввода, а ключи выводятся из состава самой воронки
 * при каждом сохранении (`sensitiveInputBlockIds`).
 *
 * Ответы с вариантами остаются: они лежат идентификаторами вариантов, а без
 * них восстановление прогресса теряет смысл — человек, обновивший страницу на
 * двадцатом шаге, проходит квиз заново и уходит.
 *
 * Второй конец правила (сторож в quizProgressStorage.test.ts): каждый тип
 * отсюда обязан быть настоящим типом-ответом по реестру
 * `answerCollection.ANSWER_BLOCK_TYPES` — иначе опечатка или переименование
 * блока делают исключение мёртвой буквой молча.
 */
export const SENSITIVE_INPUT_BLOCK_TYPES: ReadonlySet<string> = new Set([
  // Почта и телефон — прямые опознаватели человека.
  'email',
  'phone_input',
  // Вес и рост — данные о здоровье, и лежат они читаемым числом с единицей
  // измерения (companion-ключ `<id>::unit`, answerCollection.pickerUnitValueKey).
  'weight_picker',
  'height_picker',
  // ГАЛОЧКА ЯВНОГО СОГЛАСИЯ (находка 2 третьего прохода свежего взгляда,
  // 18.09). Она пишет `values[id]` как обычный ответ и потому восстанавливалась
  // сутки вместе с остальными. На общем компьютере (семья, офис, ресепшен)
  // следующий человек восстанавливался на экране предыдущего: галочка уже
  // стоит, самого экрана с ней он не видит, — а вторая галочка отпирает
  // партнёрский постбэк, серверные конверсии рекламных сетей, поток во внешнюю
  // аналитику и пиксель (WEB-2259). Спутник `<id>::share` уходит вместе с ней
  // тем же правилом про companion-ключи (`isSensitiveValueKey`).
  //
  // ⚠ РЕШЕНИЕ ПО УМОЛЧАНИЮ В ПОЛЬЗУ ЗАКРЫТОЙ ДВЕРИ, принято координатором
  // 18.09.2026 и доложено владельцу: после перезагрузки согласие спрашивается
  // ЗАНОВО, то есть по умолчанию «разрешения нет». Владелец может отменить это
  // одной строкой — убрать тип отсюда, — если решит, что согласие должно
  // переживать проход. Прохождение при этом не запирается: без галочки
  // посетителя пускают дальше (requiredGate.ts), просто ответы не уходят
  // наружу.
  SENSITIVE_CONSENT_BLOCK_TYPE,
  // Имя покупателя — расширение решения владельца (координатор пачки 45,
  // 18.09.2026, владельцу доложено как расширение): того же рода читаемые
  // личные данные, что почта и телефон. Свободный текстовый ответ
  // (`text_field`) сюда НЕ входит — он и есть ответ на вопрос, без него
  // восстановление прогресса теряет смысл.
  'name_field',
]);

/**
 * Форма, в которой хранилищу нужна воронка: только id и тип блоков. Своего
 * типа экрана (`EditorScreen`) модуль намеренно не импортирует — хранилищу
 * незачем зависеть от слоя редактора, а структурной формы достаточно.
 */
export interface FunnelScreenShape {
  blocks?: readonly { id: string; type: string }[];
}

/**
 * Id блоков этой воронки, чей ввод в браузере покупателя не лежит. Считается
 * из состава воронки — второго списка, который может отстать, нет.
 */
export function sensitiveInputBlockIds(
  screens: readonly FunnelScreenShape[] | undefined,
): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const screen of screens ?? []) {
    for (const block of screen.blocks ?? []) {
      if (SENSITIVE_INPUT_BLOCK_TYPES.has(block.type)) ids.add(block.id);
    }
  }
  return ids;
}

/**
 * Ключ принадлежит чувствительному блоку? Кроме самого id учитываются
 * companion-ключи `<id>::…` — единица измерения веса/роста
 * (`pickerUnitValueKey`) и любой будущий спутник того же блока: правило
 * «ввод этого блока в браузере не лежит» не должно обходиться приставкой.
 */
function isSensitiveValueKey(
  key: string,
  sensitiveIds: ReadonlySet<string>,
): boolean {
  if (sensitiveIds.has(key)) return true;
  const separator = key.indexOf('::');
  return separator > 0 && sensitiveIds.has(key.slice(0, separator));
}

export function filterPersistedValues(
  values: Record<string, string>,
  sensitiveIds: ReadonlySet<string>,
): Record<string, string> {
  if (sensitiveIds.size === 0) return values;
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(values)) {
    if (!isSensitiveValueKey(key, sensitiveIds)) result[key] = value;
  }
  return result;
}

/**
 * ЕДИНСТВЕННОЕ место, которое применяет решение об исключении ввода ко ВСЕМУ
 * снимку — и к ответам (`values`), и к почте незавершённой попытки
 * (`pendingLead`). `saveQuizProgress` зовёт только его: второго места, где о
 * фильтре можно забыть, по построению нет (находка 3 шестого прохода свежего
 * взгляда: решение одно, а мест ввода в снимке два).
 *
 * Цена, названная владельцем заранее: в воронке с чувствительным полем
 * недоотправленный лид перезагрузку не переживает (добивать его будет нечем —
 * адрес покупателя в браузере не лежит). Принято сознательно: штатный повтор
 * отправки всё равно живёт в памяти вкладки.
 */
export function filterPersistedSnapshot(
  snapshot: QuizProgressSnapshot,
  sensitiveIds: ReadonlySet<string>,
): QuizProgressSnapshot {
  const values = filterPersistedValues(snapshot.values, sensitiveIds);
  if (sensitiveIds.size === 0 || snapshot.pendingLead === undefined) {
    return { ...snapshot, values };
  }
  const { pendingLead: _excluded, ...rest } = snapshot;
  return { ...rest, values };
}

/**
 * Отказ от согласия на хранение — прогресс не сохраняем и не восстанавливаем
 * вовсе. Сбой самого чтения решения (см. `readConsentDecision`, читает
 * `document.cookie`) не должен блокировать поток покупателя — трактуется как
 * "решения нет", то есть хранение разрешено (fail-open здесь: это НЕ
 * согласие на европейскую атрибуцию из WEB-2146, а функциональный прогресс
 * прохождения, которого без этой записи посетитель лишится незаслуженно).
 */
function isStorageDenied(): boolean {
  try {
    return readConsentDecision() === 'denied';
  } catch {
    return false;
  }
}

/**
 * WEB-2177 (находка 2 свежего взгляда, проход 3): раньше отказ от согласия
 * (`isStorageDenied() === true`) только БЛОКИРОВАЛ новую запись/чтение —
 * запись, сделанная ДО отказа (пока баннер согласия висел и решения ещё не
 * было — хранение в этом окне разрешено), оставалась в localStorage
 * бессрочно: `removeItem` жил только внутри просрочки конкретного ключа
 * (ниже), до которой при отказе дело не доходило вовсе.
 *
 * Сметает ВСЕ ключи с префиксом `w2a_quiz_progress:` (не только текущую
 * воронку — отказ от согласия действует на посетителя целиком, не на один
 * квиз), либо только просроченные/битые, когда `removeAll` не выставлен
 * (используется при обычной загрузке — «просроченный ключ чужой воронки
 * снимается при загрузке своей», см. `loadQuizProgress`). `exceptKey` не
 * трогает ключ, который в этот же момент читает вызывающий код тем же
 * JSON.parse — незачем разбирать его дважды.
 */
function sweepQuizProgressKeys(options: {
  removeAll: boolean;
  exceptKey?: string;
}): void {
  try {
    const now = Date.now();
    const keysToRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith(KEY_PREFIX) || key === options.exceptKey) {
        continue;
      }
      if (options.removeAll) {
        keysToRemove.push(key);
        continue;
      }
      try {
        const raw = window.localStorage.getItem(key);
        const parsed: unknown = raw ? JSON.parse(raw) : undefined;
        if (
          !isValidStoredProgress(parsed) ||
          now - parsed.savedAt > QUIZ_PROGRESS_TTL_MS
        ) {
          keysToRemove.push(key);
        }
      } catch {
        keysToRemove.push(key); // битая запись чужой воронки — снимаем и её
      }
    }
    for (const key of keysToRemove) window.localStorage.removeItem(key);
  } catch {
    // Хранилище недоступно — тот же fail-open приём, что и везде в файле.
  }
}

/**
 * @param screens — состав воронки, из которого выводится, какие ключи ответов
 * в браузере не лежат (решение владельца 17.09, `SENSITIVE_INPUT_BLOCK_TYPES`).
 * Параметр обязательный намеренно: забыть его нельзя — не соберётся.
 */
export function saveQuizProgress(
  projectId: string | undefined,
  quizId: string | undefined,
  versionId: string | undefined,
  snapshot: QuizProgressSnapshot,
  screens: readonly FunnelScreenShape[] | undefined,
): void {
  if (!projectId || !quizId || !versionId) return;
  if (isStorageDenied()) {
    // WEB-2177 (находка 2): отказ пришёл ПОСЛЕ того, как запись уже
    // случилась (окно до решения) — стираем её, а не просто отказываемся
    // писать новую поверх.
    sweepQuizProgressKeys({ removeAll: true });
    return;
  }
  try {
    // Находка 3 прохода 6: ОДНА функция на весь снимок — не «values здесь, а
    // про остальные поля ввода вспомнить в следующий раз».
    const persisted = filterPersistedSnapshot(
      snapshot,
      sensitiveInputBlockIds(screens),
    );
    // НАХОДКА 3 ВТОРОГО ПРОХОДА СВЕЖЕГО ВЗГЛЯДА (18.09): запись собирается
    // ПОИМЁННО, а не спредом. Спред пропускал в localStorage любое лишнее
    // поле, оказавшееся в объекте, — а компилятор такое не ловит: проверка
    // лишних свойств работает только на литерале, и `save(load(...))` (ровно
    // то, что делает сейв-эффект визарда) проходил молча. Ближайшая жертва —
    // нехранимый признак `restartedAfterCompletion`: уехав в запись, он
    // остался бы там навсегда, и на НАСТОЯЩЕМ продолжении прохода признак
    // «почту уже отправляли» сел бы в false при живой недоотправке, то есть
    // почта ушла бы вторым лидом. Поимённый список — единственный способ
    // закрыть этот класс целиком, а не конкретное поле.
    const stored: StoredQuizProgress = {
      screenIndex: persisted.screenIndex,
      history: persisted.history,
      autoAdvanced: persisted.autoAdvanced,
      variables: persisted.variables,
      values: persisted.values,
      isCompleted: persisted.isCompleted,
      emailCaptured: persisted.emailCaptured,
      ...(persisted.pendingLead
        ? { pendingLead: persisted.pendingLead }
        : {}),
      savedAt: Date.now(),
    };
    window.localStorage.setItem(
      storageKey(projectId, quizId, versionId),
      JSON.stringify(stored),
    );
  } catch {
    // Хранилище недоступно — прогресс просто не переживёт перезагрузку;
    // поток покупателя ломать нельзя (тот же приём, что в paywallStepProof.ts).
  }
}

function isValidStoredProgress(value: unknown): value is StoredQuizProgress {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.screenIndex === 'number' &&
    typeof v.isCompleted === 'boolean' &&
    typeof v.emailCaptured === 'boolean' &&
    typeof v.savedAt === 'number' &&
    typeof v.variables === 'object' &&
    v.variables !== null &&
    // WEB-2177 (находка 1): `values` опционален на ЧТЕНИИ — запись, сделанная
    // до этой правки, его не несёт, и это не повод считать всю запись битой
    // (посетитель на день теряет только ответы, не номер экрана). Новая
    // запись (saveQuizProgress) кладёт `values` всегда.
    (v.values === undefined ||
      (typeof v.values === 'object' && v.values !== null))
    // WEB-2177 (находка 4, проход 5): `pendingLead` В ЭТУ проверку НЕ входит
    // намеренно. Поле опционально (обратная совместимость с записями до этой
    // правки, как у `values`), а его битая форма не должна обнулять ВСЮ запись:
    // номер экрана и ответы покупателя дороже одной недоотправки. Форма поля
    // проверяется на чтении (`isValidPendingLead` в `loadQuizProgress`) и
    // негодное значение просто не восстанавливается.
  );
}

/**
 * WEB-2177 — УБОРКА ДЛЯ ТЕХ, КТО НЕ ВЕРНУЛСЯ (второй пункт решения владельца
 * 17.09.2026).
 *
 * До этой правки уборка жила ВНУТРИ чтения и записи прогресса: просроченное
 * снималось, только когда покупатель возвращался и страница звала
 * `loadQuizProgress` со всеми тремя анкерами (проект, квиз, версия). Ровно в
 * самом частом случае — бросил квиз и не вернулся — уборка не срабатывала
 * никогда, и запись лежала в браузере не сутки, а бессрочно. Тот же провал у
 * записи без анкера версии и на страницах, которые прогресс не читают вовсе.
 *
 * Поэтому уборка становится отдельным действием и зовётся при ЛЮБОЙ загрузке
 * публичной страницы (PublicQuiz.tsx), независимо от того, та ли это воронка.
 *
 * ПОЧЕМУ ЭТОГО ДОСТАТОЧНО И ПОЧЕМУ БОЛЬШЕГО НЕТ. Другого фона у браузера для
 * нас не существует: наш код в этом браузере выполняется только тогда, когда
 * человек открыл нашу страницу, — ни планировщика, ни служебного процесса у
 * страницы нет (Service Worker мы не ставим, и он бы тоже просыпался лишь по
 * событию). Значит «уборка при любой нашей загрузке» — максимум возможного со
 * стороны браузера; человека, который больше никогда не откроет ни одной
 * нашей воронки, достаёт только собственная уборка браузера (очистка данных
 * сайта). Это и есть граница, названная честно, а не обещание фона, которого
 * нет.
 */
export function sweepExpiredQuizProgress(): void {
  sweepQuizProgressKeys({ removeAll: false });
}

/**
 * ЖАЛОБА ПОКУПАТЕЛЯ 18.09.2026 (воронка funnel.quizsomaapp.info) — ЗАВЕРШЁННЫЙ
 * ПРОХОД ВОССТАНАВЛИВАЛСЯ ПО ЛЮБОЙ ССЫЛКЕ И ЗАПИРАЛ КВИЗ НА СУТКИ.
 *
 * Человек один раз дошёл до пейволла → в снимок лёг `isCompleted: true` на
 * сутки → ЛЮБОЙ следующий заход по той же рекламной ссылке (чистый адрес, без
 * метки `?s=paywall`) сразу открывал экран оплаты: пройти квиз было нельзя, а
 * владелец воронки платил за клики, которые не доходили до прохождения.
 *
 * Обходилась при этом двойная защита WEB-1740 («попал сразу на оплату»:
 * метка `?s=paywall` в адресе И след настоящего перехода в хранилище,
 * `paywallStepProof`). Защита стоит на пути показа пейволла, а восстановление
 * прогресса заходило мимо неё — поэтому судить о завершённости надо здесь же,
 * по тому же признаку «страница открыта ШТАТНЫМ шагом оплаты».
 *
 * @param options.atPaywallStep — страница открыта штатной меткой шага оплаты
 * (и след WEB-1740 на месте). Параметр обязательный намеренно: забыть его
 * нельзя — не соберётся (тот же приём, что у `screens` в `saveQuizProgress`).
 * Оба места вызова (QuizScreenWizard.tsx и PublicQuiz.tsx) обязаны передавать
 * ОДНО И ТО ЖЕ значение — второго источника правды здесь быть не должно.
 */
export function loadQuizProgress(
  projectId: string | undefined,
  quizId: string | undefined,
  versionId: string | undefined,
  options: { atPaywallStep: boolean },
): RestoredQuizProgress | undefined {
  if (!projectId || !quizId || !versionId) return undefined;
  if (isStorageDenied()) {
    // WEB-2177 (находка 2): та же сметка, что и в saveQuizProgress — отказ,
    // случившийся между сохранением и следующей загрузкой (например, новая
    // вкладка/перезагрузка), обязан стереть след, а не просто отказать этому
    // конкретному чтению.
    sweepQuizProgressKeys({ removeAll: true });
    return undefined;
  }
  const key = storageKey(projectId, quizId, versionId);
  // WEB-2177 (находка 2): попутно снимаем просроченное/битое у ДРУГИХ
  // воронок этого же браузера — раньше уборка происходила только внутри
  // чтения СВОЕГО ключа (ниже), и ключ квиза, который посетитель больше не
  // открывает, простаивал в localStorage бессрочно.
  sweepQuizProgressKeys({ removeAll: false, exceptKey: key });
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (!isValidStoredProgress(parsed)) {
      window.localStorage.removeItem(key);
      return undefined;
    }
    if (Date.now() - parsed.savedAt > QUIZ_PROGRESS_TTL_MS) {
      window.localStorage.removeItem(key);
      return undefined;
    }
    const {
      screenIndex,
      history,
      autoAdvanced,
      variables,
      values,
      isCompleted,
      emailCaptured,
      pendingLead,
    } = parsed;
    const restoredPendingLead = isValidPendingLead(pendingLead)
      ? { pendingLead }
      : {};
    // Запись, за которой прохождения уже нет (ветка сброса ниже оставила в ней
    // одну недоотправку): читающий обязан узнать в ней НОВЫЙ проход, иначе
    // признак «почту уже отправляли» погасит отправку на этом проходе.
    if (parsed.leadOnly === true) {
      return {
        screenIndex: 0,
        history: [],
        autoAdvanced: [],
        variables: {},
        values: {},
        isCompleted: false,
        emailCaptured: false,
        restartedAfterCompletion: true,
        ...restoredPendingLead,
      };
    }
    if (isCompleted && !options.atPaywallStep) {
      // Жалоба 18.09: завершённый проход без штатной метки шага оплаты — это
      // новый заход по рекламной ссылке, а не возврат на оплату. Начинаем
      // заново.
      //
      // ИСКЛЮЧЕНИЕ — недоотправленный лид: контакт покупателя терять нельзя,
      // добивание не зависит от того, показываем мы пейволл или нет
      // (WEB-1716 / WEB-2177, находка 4 пятого прохода свежего взгляда).
      //
      // НАХОДКА 1 ТРЕТЬЕГО ПРОХОДА СВЕЖЕГО ВЗГЛЯДА — ЗАПИСЬ СНИМАЕТСЯ, А НЕ
      // ЗАТИРАЕТСЯ ПУСТОТОЙ. Раньше сюда возвращался пустой снимок, и
      // сейв-эффект визарда тут же записывал его поверх настоящего: ответы
      // стирались, а право на быструю перемотку (метка `?s=paywall` + след
      // WEB-1740) жило ещё сутки. Человек, вернувшийся на адрес с меткой
      // (кнопка «назад», вкладка с оплатой, адрес возврата платёжной
      // системы), попадал на экран оплаты БЕЗ ответов — а по ответам считаются
      // правила показа блоков пейволла и выбор тарифа, то есть он видел не то
      // предложение, которое ему собрали.
      //
      // Теперь нет прохода — нет и записи. Вторую половину того же факта
      // (след шага оплаты) снимает страница: якорь следа — `проект:слаг`, а
      // этому модулю слаг неизвестен, его якорь — `проект:квиз:версия`.
      // Единственное, что переживает снятие, — недоотправка: она хранится
      // сама по себе, без ответов, признака завершённости и номера экрана,
      // чтобы её увидел и добил ВТОРОЙ читающий той же загрузки (визард
      // читает после страницы).
      if (restoredPendingLead.pendingLead) {
        window.localStorage.setItem(
          key,
          JSON.stringify({
            screenIndex: 0,
            history: [],
            autoAdvanced: [],
            variables: {},
            values: {},
            isCompleted: false,
            emailCaptured: false,
            pendingLead: restoredPendingLead.pendingLead,
            leadOnly: true,
            savedAt: Date.now(),
          } satisfies StoredQuizProgress),
        );
      } else {
        window.localStorage.removeItem(key);
      }
      return {
        screenIndex: 0,
        history: [],
        autoAdvanced: [],
        variables: {},
        values: {},
        isCompleted: false,
        emailCaptured: false,
        // Находка 2 свежего взгляда: читающий обязан знать, что это НОВЫЙ
        // проход, а не продолжение — иначе признак «почту уже отправляли»
        // поднимется от старой недоотправки и погасит отправку на этом проходе
        // (см. докблок RestoredQuizProgress).
        restartedAfterCompletion: true,
        ...restoredPendingLead,
      };
    }
    return {
      screenIndex,
      history: readHistory(history),
      autoAdvanced: readAutoAdvanced(autoAdvanced),
      variables,
      values: values ?? {},
      isCompleted,
      emailCaptured,
      ...restoredPendingLead,
    };
  } catch {
    return undefined;
  }
}
